/**
 * Profile Header — avatar, name, badge, stats, bio, action buttons.
 * Stats are tappable to open followers/following lists.
 */

import React, { useState, useCallback } from 'react'
import { UserPlus, UserCheck, Clock, MessageCircle, Link as LinkIcon } from 'lucide-react'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { AvatarWithBadge, getBadgeDisplay } from '@turnifly/shared/ui'
import { Button } from '@turnifly/shared/ui'
import { Badge } from '@turnifly/shared/ui'
import { FollowListSheet } from './follow-list-sheet'
import { PremiumPage } from './premium-page'
import { useAuthStore } from '@turnifly/shared/stores'
import { useToggleFollow } from '@turnifly/shared/hooks'
import { findOrCreateDirectConversation } from '@turnifly/shared/api'
import { triggerHapticImpact as hapticImpact } from '@turnifly/shared/utils'
import type { Profile } from '@turnifly/shared/types'
import type { FollowStatusResult } from '@turnifly/shared/api'

interface ProfileHeaderProps {
  profile: Profile
  isOwnProfile: boolean
  followStatus?: FollowStatusResult
  onEditProfile: () => void
}

type FollowListMode = 'followers' | 'following' | null

export const ProfileHeader: React.FC<ProfileHeaderProps> = ({
  profile, isOwnProfile, followStatus, onEditProfile,
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user: authUser } = useAuthStore()
  const toggleFollow = useToggleFollow()
  const [followListMode, setFollowListMode] = useState<FollowListMode>(null)
  const [isNavigatingToChat, setIsNavigatingToChat] = useState(false)
  const [showPremium, setShowPremium] = useState(false)

  const badge = getBadgeDisplay(profile.is_premium, profile.badge_type)

  const displayName = profile.verified_name ?? profile.full_name
  const displayUsername = profile.verified_username ?? profile.username

  // DM visible when: follow accepted + not blocked (premium checked on click)
  const canShowDm = Boolean(
    followStatus?.followStatus === 'accepted'
    && !followStatus?.isBlockedByTarget,
  )
  const isPremium = Boolean(authUser?.is_premium)

  const handleFollowToggle = (): void => {
    if (!authUser?.id || toggleFollow.isPending) return
    hapticImpact('light')
    toggleFollow.mutate({
      followerId: authUser.id,
      followingId: profile.id,
      isTargetPrivate: profile.is_private,
      isCurrentlyFollowing: followStatus?.followStatus !== 'none',
    })
  }

  const handleMessageClick = useCallback(async (): Promise<void> => {
    if (!authUser?.id || isNavigatingToChat) return

    // Free users → open premium page
    if (!isPremium) {
      hapticImpact('light')
      setShowPremium(true)
      return
    }

    try {
      setIsNavigatingToChat(true)
      hapticImpact('light')
      const conversation = await findOrCreateDirectConversation(authUser.id, profile.id)
      navigate(`/inbox/chat/${conversation.id}`, {
        state: { participantName: profile.full_name, avatarUrl: profile.avatar_url },
      })
    } catch {
      hapticImpact('medium')
    } finally {
      setIsNavigatingToChat(false)
    }
  }, [authUser?.id, profile.id, profile.full_name, profile.avatar_url, navigate, isNavigatingToChat, isPremium])

  const handleLinkOpen = (): void => {
    if (profile.link_url) {
      window.open(profile.link_url, '_blank', 'noopener')
    }
  }

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="px-4 pt-2"
      >
        {/* Avatar + Name */}
        <div className="flex flex-col items-center text-center">
          <AvatarWithBadge
            src={profile.avatar_url}
            fallback={displayName.charAt(0).toUpperCase()}
            size="xl"
            badge={badge}
          />

          <div className="mt-3 flex items-center gap-1.5">
            <h2 className="text-xl font-display font-bold text-text">{displayName}</h2>
            {profile.badge_type === 'expert' && (
              <Badge variant="primary" className="text-[10px] px-1.5 py-0">
                {t('profile.expert')}
              </Badge>
            )}
          </div>

          {displayUsername && (
            <p className="text-sm text-text-hint mt-0.5">@{displayUsername}</p>
          )}

          {/* Stats Row — tappable */}
          <div className="flex items-center gap-6 mt-4">
            <StatItem
              value={profile.goals_count}
              label={t('profile.goalsLabel')}
            />
            <div className="w-px h-6 bg-white/[0.06]" />
            <StatItem
              value={profile.followers_count}
              label={t('profile.followers')}
              onClick={() => setFollowListMode('followers')}
            />
            <div className="w-px h-6 bg-white/[0.06]" />
            <StatItem
              value={profile.following_count}
              label={t('profile.following')}
              onClick={() => setFollowListMode('following')}
            />
          </div>

          {/* Bio */}
          {profile.bio && (
            <p className="text-sm text-text/80 mt-3 max-w-xs leading-relaxed">{profile.bio}</p>
          )}

          {/* Link */}
          {profile.link_url && (
            <button
              onClick={handleLinkOpen}
              className="flex items-center gap-1 mt-2 text-xs text-primary hover:text-accent transition-colors tap-highlight-transparent"
            >
              <LinkIcon size={12} />
              <span className="truncate max-w-[200px]">
                {profile.link_title ?? profile.link_url}
              </span>
            </button>
          )}

          {/* Action Buttons */}
          <div className="flex items-center gap-3 mt-4 w-full max-w-xs">
            {isOwnProfile ? (
              <Button variant="glass" size="sm" fullWidth onClick={onEditProfile} className="font-semibold">
                {t('profile.editProfile')}
              </Button>
            ) : (
              <>
                <FollowButton
                  followStatus={followStatus}
                  isLoading={toggleFollow.isPending}
                  onClick={handleFollowToggle}
                />
                {canShowDm && (
                  <Button
                    variant="glass"
                    size="sm"
                    className="w-10 !px-0"
                    disabled={isNavigatingToChat}
                    onClick={handleMessageClick}
                  >
                    <MessageCircle size={16} className={isNavigatingToChat ? 'animate-pulse' : ''} />
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      </motion.div>

      {/* Follow List Sheet */}
      <FollowListSheet
        isOpen={followListMode !== null}
        onClose={() => setFollowListMode(null)}
        userId={profile.id}
        mode={followListMode ?? 'followers'}
      />

      {/* Premium upsell for free users trying to DM */}
      <PremiumPage isOpen={showPremium} onClose={() => setShowPremium(false)} />
    </>
  )
}

// ── Sub-components ──────────────────────────────────────────

interface StatItemProps {
  value: number
  label: string
  onClick?: () => void
}

const StatItem: React.FC<StatItemProps> = React.memo(({ value, label, onClick }) => (
  <button
    onClick={onClick}
    className="flex flex-col items-center tap-highlight-transparent"
    disabled={!onClick}
  >
    <span className="text-lg font-display font-bold text-text">{formatCount(value)}</span>
    <span className="text-xs text-text-hint">{label}</span>
  </button>
))
StatItem.displayName = 'StatItem'

interface FollowButtonProps {
  followStatus?: FollowStatusResult
  isLoading: boolean
  onClick: () => void
}

const FollowButton: React.FC<FollowButtonProps> = ({ followStatus, isLoading, onClick }) => {
  const { t } = useTranslation()

  if (!followStatus || followStatus.followStatus === 'none') {
    return (
      <Button variant="primary" size="sm" className="flex-1" isLoading={isLoading} onClick={onClick}>
        <UserPlus size={14} className="mr-1.5" />
        {t('profile.follow')}
      </Button>
    )
  }

  if (followStatus.followStatus === 'pending') {
    return (
      <Button variant="glass" size="sm" className="flex-1" isLoading={isLoading} onClick={onClick}>
        <Clock size={14} className="mr-1.5" />
        {t('profile.requested')}
      </Button>
    )
  }

  return (
    <Button variant="glass" size="sm" className="flex-1" isLoading={isLoading} onClick={onClick}>
      <UserCheck size={14} className="mr-1.5" />
      {t('profile.following')}
    </Button>
  )
}

function formatCount(count: number): string {
  if (count >= 1_000_000) {
    const val = count / 1_000_000
    return val % 1 === 0 ? `${val}M` : `${val.toFixed(1)}M`
  }
  if (count >= 1_000) {
    const val = count / 1_000
    return val % 1 === 0 ? `${val}K` : `${val.toFixed(1)}K`
  }
  return count.toString()
}

